import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import fetchSession from '../utils/session/fetchSession';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const pollSession = async (uuid) => {
  console.log('[useSessionPolling] Starting polling for session:', uuid);
  const startTime = Date.now();
  const timeout = 300000; // 5 minutes timeout

  while (Date.now() - startTime <= timeout) {
    const session = await fetchSession(uuid);
    console.log('[useSessionPolling] Polling check - session ready:', session.isReady());
    if (session.isErrored()) return session; // Stop polling if errored, but do not treat as ready
    if (session.isReady()) return session;
    await sleep(2000);
  }

  console.error('[useSessionPolling] Polling timeout reached');
};

export default function useSessionPolling(onSessionReady) {
  const isMounted = useRef(true);
  const [activeSession, setActiveSession] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const uuid = query.get('active_uuid');
    console.log('[useSessionPolling] active_uuid param:', uuid);
    
    if (uuid) {
      // Remove the active_uuid parameter immediately
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete('active_uuid');
      navigate(`${location.pathname}${newQuery.toString() ? `?${newQuery.toString()}` : ''}`, { replace: true });

      // Start polling without showing generating state
      console.log('[useSessionPolling] Initiating pollSession for uuid:', uuid);
      pollSession(uuid)
        .then(async (updatedSession) => {
          if (!isMounted.current) return; // Stop if component unmounted
          
          console.log('[useSessionPolling] Polling completed, session ready:', updatedSession);
          
          // Call the callback with the updated session
          if (onSessionReady) {
            onSessionReady(updatedSession);
          }

          // Show highlight
          setActiveSession(updatedSession);
          // Remove the highlight after 20 seconds with a smooth fade
          setTimeout(() => {
            if (!isMounted.current) return; // Stop if component unmounted
            setActiveSession(null);
          }, 20000);
        })
        .catch(error => {
          console.error('[useSessionPolling] Polling failed:', error);
          // Silently handle the error - don't update any state
        });
    }
  }, [location.search, navigate, location.pathname, onSessionReady]);

  return activeSession;
} 